import React, { useEffect, useMemo, useState } from "react";
import { Paper, Chip, Tooltip, TextField as TextFieldMaterialUI } from "@mui/material";
import { ThemeProvider, createTheme, alpha, darken, lighten } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import MaterialTable from "@material-table/core";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { Autocomplete } from "@mui/lab";

import {
  addOrEditFilterValue,
  deleteFilterValue,
  getFilterValues,
  getFilterValuesById,
} from "../../../store/ducks/duckFilterValue";
import { getGroups } from "../../../store/ducks/duckGroups";
import { useStylesLayout } from "../../../styles/Layout";
import { BorderLinearProgress } from "../../../components/LoadingComponent";
import { useUser } from "../../../auth/UserProvider";
import { clearFilterValueTable } from "../../../store/ducks/duckLayoutFilter";
import { getFilters } from "../../../store/ducks/duckFilter";
import NavBar from "../../../components/Navbar";

const FilterValue = () => {
  const classes = useStylesLayout();
  const { enqueueSnackbar } = useSnackbar();
  const [allLoaded, setAllLoaded] = useState(false);

  const { filterId } = useParams();

  const filterName = useSelector(
    (state) => state.filter.data.find((x) => x.id === filterId)?.name ?? "",
  );
  const {
    dataFilterValue,
    isLoading: dataFilterValueLoading,
    data,
  } = useSelector((state) => state.filterValue);

  const dataFilterLookup = useSelector((state) => {
    let data = state.filter.data;
    let dataL = {};

    data.forEach((x) => {
      dataL[x.id] = `${x.name}${x.tag ? `: ${x.tag}` : ""}`;
    });

    return dataL;
  });

  const groupsFilters = useSelector((state) => state.groups.data).sort((a, b) =>
    a.name > b.name ? 1 : -1,
  );

  const dataGroupsArray = useSelector((state) => {
    let data = state.groups.data.sort((a, b) => (a.name > b.name ? 1 : -1));
    let dataL = [];

    data.forEach((x, index) => {
      dataL[index] = x;
    });

    return dataL;
  });

  const getDataGroupsLabel = (id) => {
    let dataL = dataGroupsArray.find((x) => x.id === id)?.name;

    return dataL;
  };
  const dataFilterValueArray = useSelector((state) => {
    let data = state.filterValue.data.sort((a, b) => (a.label > b.label ? 1 : -1));

    let dataL = [];

    data.forEach((x, index) => {
      dataL[index] = x;
    });

    return dataL;
  });
  const getDataParentsLabel = (Id) => {
    let dataL = dataFilterValueArray.find((x) => x.id === Id);
    let newData = dataL.parentValue
      ? `${dataL.label} (${dataL.filter}) - ${dataL.parentValue} -  Filter Id : ${dataL.id}`
      : `${dataL.label} (${dataL.filter}) -  Filter Id : ${dataL.id}`;

    return newData;
  };

  const getDataParentsId = () => dataFilterValueArray.map((x) => x.id);
  const getDataGroupsId = () => dataGroupsArray.map((x) => x.id);
  const { isAdmin } = useUser();
  const filters = useSelector((state) =>
    state.filter.data.map((value) => ({ id: value.id, name: value.name })),
  );

  const dispatch = useDispatch();

  useEffect(() => {
    let promAux;

    if (filterId && filterId !== "") {
      promAux = dispatch(getFilterValuesById(filterId));
    } else {
      promAux = dispatch(getFilterValues());
    }

    Promise.all([promAux, dispatch(getGroups()), dispatch(getFilters())]).then(() =>
      setAllLoaded(true),
    );

    return () => dispatch(clearFilterValueTable());
  }, [filterId, dispatch]);

  useEffect(() => {
    if (
      filterId &&
      filterId !== "" &&
      dataFilterValue.length === 0 &&
      !dataFilterValueLoading &&
      allLoaded
    ) {
      enqueueSnackbar("This Filter not have filtervalues assigned", {
        variant: "info",
      });
    }
  }, [filterId, dataFilterValue, dataFilterValueLoading, dispatch, enqueueSnackbar, allLoaded]);

  const materialTableTheme = useMemo(() => {
    const theme = createTheme();

    theme.alpha = alpha;
    theme.darken = darken;
    theme.lighten = lighten;

    return theme;
  }, []);

  return (
    <>
      <NavBar />
      <Paper className={classes.administrationPaper}>
        {dataFilterValueLoading && <BorderLinearProgress />}
        <ThemeProvider theme={materialTableTheme}>
          <MaterialTable
          columns={[
            {
              title: "Filter",
              field: "filterId",
              align: "left",
              hidden: filterId && filterId !== "",
              editable: () => filterId && filterId !== "",
              render: (rowData) => {
                if (!rowData.filterId) return null;
                let filterName = filters.find((x) => x.id === rowData.filterId)?.name;

                if (filterName) {
                  return (
                    <Chip
                      key={`filterName-${filterName}-${rowData.id}`}
                      label={filterName}
                      variant="outlined"
                    />
                  );
                }
              },
              lookup: dataFilterLookup,
            },
            {
              title: "Id",
              field: "id",
              hiddenByColumnsButton: true,
              align: "justify",
              editable: "never",
            },
            {
              title: "Label",
              field: "label",
              align: "right",
              validate: (rowData) =>
                rowData.label == null ? { isValid: false, helperText: "Required" } : true,
            },
            {
              title: "Value",
              field: "value",
              align: "left",
              validate: (rowData) =>
                rowData.value == null ? { isValid: false, helperText: "Required" } : true,
            },
            {
              title: "Order",
              field: "order",
              type: "numeric",
              align: "justify",
            },
            {
              title: "FilterValueParent",
              field: "filterValueParentId",
              editComponent: (x) => {
                return (
                  <Autocomplete
                    key={`ParentFilter${x.value}`}
                    getOptionLabel={(option) => getDataParentsLabel(option)}
                    id="demo-parent"
                    options={getDataParentsId()}
                    renderInput={(params) => {
                      return (
                        <TextFieldMaterialUI
                          {...params}
                          key={`TextField Parent- ${x.value}`}
                          variant="outlined"
                        />
                      );
                    }}
                    size="small"
                    value={x.rowData.filterValueParentId}
                    onChange={(e, newValue) => {
                      x.onChange(newValue);
                    }}
                  />
                );
              },
            },
            { title: "Public", field: "public", type: "boolean" },
            {
              title: "Deleted",
              field: "deleted",
              type: "boolean",
              hidden: !isAdmin,
              editable: isAdmin ? "onUpdate" : "never",
              defaultFilter: "unchecked",
              filtering: true,
              hiddenByColumnsButton: isAdmin,
            },
            {
              title: "Groups",
              field: "groups",
              render: (rowData) => {
                if (!rowData.groups) return null;

                if (rowData.public) {
                  return <></>;
                }

                return (
                  <>
                    {rowData.groups?.map((row) => {
                      let groupFilter = groupsFilters?.find((x) => x.id === row);

                      return (
                        groupFilter &&
                        groupFilter.name && (
                          <Tooltip title={groupFilter.name ?? ""}>
                            <Chip
                              className={classes.chipGroups}
                              label={groupFilter.name?.slice(0, 1)}
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )
                      );
                    })}
                  </>
                );
              },
              editComponent: (x) => {
                return (
                  <Autocomplete
                    key={`Groups${x.value}`}
                    getOptionLabel={(option) => getDataGroupsLabel(option)}
                    id="demo-mutiple-chip"
                    multiple={true}
                    options={getDataGroupsId()}
                    renderInput={(params) => {
                      return (
                        <TextFieldMaterialUI
                          {...params}
                          key={`TextField groups- ${x.value}`}
                          variant="outlined"
                        />
                      );
                    }}
                    size="small"
                    value={x.rowData.groups}
                    onChange={(event, newValue) => {
                      x.onChange(newValue);
                    }}
                  />
                );
              },
            },
            {
              title: "Default",
              field: "default",
              type: "boolean",
              align: "justify",
              validate: (rowData) =>
                dataFilterValue &&
                rowData.default &&
                dataFilterValue?.some((x) => x.default && x.id !== rowData.id)
                  ? "Another filter value is already by default"
                  : true,
            },
          ]}
          components={{
            Container: (props) => <Paper {...props} elevation={0} />,
          }}
          data={filterId ? dataFilterValue : data}
          editable={{
            onRowAdd: (newData) =>
              dispatch(
                addOrEditFilterValue(
                  {
                    ...newData,
                    filterId: filterId ?? newData.filterId,
                    groups: newData.groups || [],
                  },
                  enqueueSnackbar("Filter value added Succesfully", {
                    variant: "success",
                  }),
                ),
              ),
            onRowUpdate: (newData, _oldData) =>
              dispatch(
                addOrEditFilterValue(
                  newData,
                  enqueueSnackbar("Filter value Edited Succesfully", {
                    variant: "success",
                  }),
                ),
              ),
            onRowDelete: (oldData) =>
              dispatch(
                deleteFilterValue(
                  oldData.id,
                  filterId,
                  enqueueSnackbar("Filter value Deleted Succesfully", {
                    variant: "success",
                  }),
                ),
              ),
            isDeletable: (rowData) => !rowData.deleted,
          }}
          isLoading={dataFilterValueLoading || !allLoaded}
          options={{
            actionsColumnIndex: -1,
            rowStyle: (rowData) => {
              let deleted = rowData?.deleted;

              return (
                deleted && {
                  backgroundColor: "Gainsboro",
                  title: "Deleted",
                }
              );
            },
            columnsButton: true,
            filtering: true,
          }}
          padding={"dense"}
          title={`Filter Values.${filterName && ` Filter: ${filterName}`}`}
        />
        </ThemeProvider>
      </Paper>
    </>
  );
};

export default FilterValue;
